/* Copyright (c) 2020-present CloudIO Inc */

import { IoLookups, Query } from '@cloudio-saas/datasource-types';
import { useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LookupCodeChooserProp, LookupValueType } from 'componentregistry';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import React, { memo, useEffect } from 'react';
import { useStoreBusy } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import { getIoLookupsStoreProps } from 'store/utils/utils';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

export const dsFilter: () => Query<IoLookups> = () => ({
  filter: [],
  limit: 5000,
  offset: 0,
  projection: {
    lookupUid: 1,
    isNumber: 1,
  },
});

function LookupCodeChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<LookupValueType>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<LookupCodeChooserProp, LookupValueType>) {
  const store = useDataStore<IoLookups>(getIoLookupsStoreProps(appUid));
  useEffect(() => {
    store.query({ props: dsFilter() });
  }, [store]);
  const records = useStoreRecords(store);
  const busy = useStoreBusy(store);
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (code: string, option: Option | null) => {
    if (code !== value.code) {
      onChange(
        property,
        option ? { code, isNumeric: option.isNumber === 'Y' } : undefined,
      );
    }
  };
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      data-testid={property}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value.code}
        options={records}
        onChange={handleChange}
        label={def.label}
        startIcon={busy ? <WaveDots /> : null}
        disabled={busy || isViewOnlyUser}
        placeholder={busy ? 'Loading ...' : 'Choose a Lookup Code'}
        variant="standard"
        valueAttribute="lookupUid"
        labelAttribute="lookupUid"
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export default memo(LookupCodeChooserProperty);
